import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import { ArrowLeft, Building2, Home, LandPlot, MapPin, Pencil, Search, X } from 'lucide-react';
import { WarsawMetroIcon } from '@/components/offer/WarsawMetroBadge';
import { EkranStacjiMetra } from './EkranStacjiMetra';
import { WyborDzielnic } from './WyborDzielnic';
import { WARSAW_METRO } from '@/data/warsawMetro';
import { WARSAW_METRO_DISTRICT } from '@/data/warsawMetroDistricts';
import { STATION_RADIUS_M, areaKey, etykietaObszaru, nazwaObszaru, searchAreas, type SearchArea } from '@/lib/areaSearch';
import { MAX_OBSZAROW, dodajObszar, usunObszar } from '@/lib/areaSelection';
import { useZapisBrakuPodpowiedzi } from '@/lib/brakiWyszukiwania';
import { stanLicznika } from '@/lib/serpCountLabel';
import { formatLiczby } from '@/lib/i18nNumber';
import { useSerpCount, type SourcePostFilters } from '@/hooks/useSourcePosts';
import { czytajHistorie, filtryZWpisu, usunWpis, wyczyscHistorie } from '@/lib/historiaWyszukiwan';
import { HistoriaWyszukiwan } from './HistoriaWyszukiwan';
import type { StacjaNaLiscie } from '@/lib/stacjeMetra';
import { ListaMiejscWPolu, WierszSzukajMiejsca } from './MiejscaWPolu';
import { useOstatnieMiejsca, type Szukaj } from '@/hooks/useBliskoDo';
import { useMiejsceWPolu } from '@/hooks/useMiejsceWPolu';
import { obszarZMiejsca, type PodpowiedzMiejsca } from '@/lib/bliskoDo';
import { klasyZastapienia, useBlyskZastapienia } from '@/hooks/useBlyskZastapienia';

interface Props {
  areas: SearchArea[];
  onChange: (next: SearchArea[]) => void;
  onClose: () => void;
  /** Pozostałe ustawione filtry (cena, pokoje, metraż…) — licznik na przycisku ma
   *  pokazywać wynik DLA CAŁEGO wyszukiwania, a nie dla samego obszaru. */
  filters?: SourcePostFilters;
  /** Przejście do rysowania obszaru na mapie (przełącza też widok na mapę). */
  onDrawArea?: () => void;
  /** Przejście do klikania dzielnic na mapie. */
  onPickDistricts?: () => void;
  /** Wybór wpisu z historii wyszukiwań — przywraca CAŁY stan (obszary, cenę, pokoje…). */
  onPrzywroc?: (f: SourcePostFilters) => void;
  /** Wyszukiwanie miejsc (domyślnie `szukajMiejsc` → `place-autocomplete`); testy podstawiają atrapę. */
  szukajMiejsc?: Szukaj;
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Jeden dotykalny wybór. Świadomie `button`, a nie pole — patrz nagłówek pliku. */
function Chip({
  label,
  sublabel,
  icon,
  active,
  onClick,
}: {
  label: string;
  sublabel?: string | null;
  icon?: React.ReactNode;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex min-h-11 items-center gap-1.5 rounded-full border px-3.5 py-2 text-left text-sm transition-colors ${
        active
          ? 'border-primary bg-primary/10 font-medium text-foreground'
          : 'border-border text-foreground hover:border-foreground/30'
      }`}
    >
      {icon}
      <span>{label}</span>
      {sublabel && <span className="text-xs text-muted-foreground">· {sublabel}</span>}
    </button>
  );
}

function Sekcja({ tytul, children }: { tytul: string; children: React.ReactNode }) {
  return (
    <section className="px-4 py-3">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {tytul}
      </h2>
      <div className="flex flex-wrap gap-2">{children}</div>
    </section>
  );
}

/**
 * Wybór obszaru na telefonie — pełny ekran, wybór DOTYKIEM, pisanie jako druga droga.
 *
 * Zasada Piotra (2026-09-26): tam, gdzie nie trzeba pisać, nie wywołujemy klawiatury.
 * Dlatego pole w nagłówku wyszukiwarki jest PRZYCISKIEM wyglądającym jak pole, a ta
 * nakładka otwiera się z gotowymi sekcjami do dotknięcia i BEZ automatycznego fokusu na
 * polu. Klawiatura pojawia się dopiero wtedy, gdy człowiek sam dotknie pola — a wcześniej
 * zabierała pół ekranu mapie i wypychała listę podpowiedzi poza widok.
 *
 * DECYZJA o zamykaniu, bo te dwa przypadki nie są takie same:
 *   • wybór z PODPOWIEDZI po wpisaniu zamyka nakładkę — człowiek wpisał jedną konkretną
 *     rzecz i jest gotowy;
 *   • dotknięcie CHIPA w sekcjach zostawia nakładkę otwartą — przeglądanie jest z natury
 *     wielokrotne i zamykanie po pierwszej dzielnicy zmuszałoby do otwierania od nowa.
 * Wybrane obszary widać na górze przez cały czas, a stopka ma jeden przycisk wyjścia.
 *
 * Desktop tego nie używa — tam zostaje lista pod polem (SearchAreaBar).
 */
const SearchAreaOverlay = ({ areas, onChange, onClose, onDrawArea, onPickDistricts, onPrzywroc, filters = {}, szukajMiejsc }: Props) => {
  const { t, i18n } = useTranslation() as { t: Translate; i18n: { language: string } };
  // Pole z trybem miejsc — ten sam stan i ta sama ścieżka wyszukiwania, co na pulpicie (plan §1.90).
  const pole = useMiejsceWPolu(szukajMiejsc);
  const { query } = pole;
  const ostatnieMiejsca = useOstatnieMiejsca();
  const inputRef = useRef<HTMLInputElement>(null);
  // HISTORIA WYSZUKIWAŃ (28.09) zamiast dawnych „Ostatnio szukane" z samymi obszarami — stare
  // wpisy migrują w `czytajHistorie`. Czytane RAZ przy otwarciu: lista nie przeskakuje pod
  // palcem, gdy człowiek właśnie coś wybiera; zmienia się tylko przez „×" i „Wyczyść".
  const [historia, setHistoria] = useState(() => czytajHistorie());
  /** Poziom nakładki. Pełna lista stacji to OSOBNY ekran, nie druga sekcja poziomu
   *  głównego — ten zostaje przy Ostatnio szukane / Dzielnice / Na mapie. */
  const [ekran, setEkran] = useState<'glowny' | 'stacje'>('glowny');
  const [limit, setLimit] = useState(false);

  const wybrane = useMemo(() => new Set(areas.map(areaKey)), [areas]);
  // Podgląd i błysk zastąpienia jak w polu na pulpicie (plan §1.91). Na dotyku lista dzielnic nie
  // podgląda (brak hovera), ale wąskie okno z myszą też dostaje tę nakładkę — tam podgląd działa.
  const [przygaszone, setPrzygaszone] = useState<string[]>([]);
  const blysk = useBlyskZastapienia(areas);

  // Liczba wyników na żywo. Zwłoka 300 ms, bo wybór obszaru to seria dotknięć, a bez niej
  // każde z nich wysyłałoby zapytanie, którego wynik i tak zdążyłby się zdezaktualizować.
  const [areasDoLiczenia, setAreasDoLiczenia] = useState(areas);
  useEffect(() => {
    const t = setTimeout(() => setAreasDoLiczenia(areas), 300);
    return () => clearTimeout(t);
  }, [areas]);
  // TEN SAM hook, którego używa lista i arkusz filtrów (useSerpCount → serpSelect +
  // applySerpFilters). Nie własne zapytanie: druga kopia predykatów rozjechałaby się z
  // listą przy pierwszej zmianie filtra i przycisk obiecywałby inną liczbę, niż pokaże.
  const licznik = useSerpCount({ ...filters, areas: areasDoLiczenia.length ? areasDoLiczenia : undefined });
  const stan = stanLicznika(licznik.data, licznik.isFetching);
  const podpowiedzi = query.trim().length >= 2
    ? searchAreas(query).filter((s) => !wybrane.has(areaKey(s.area)))
    : [];
  const pisze = query.trim().length >= 2;
  // Jak w polu na pulpicie: fraza bez podpowiedzi idzie do przeglądu braków (19.10).
  useZapisBrakuPodpowiedzi(query, pisze && podpowiedzi.length === 0);

  // Escape zamyka; blokada przewijania tła, żeby nakładka nie przewijała strony pod sobą.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const poprzedni = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = poprzedni;
    };
  }, [onClose]);

  const dodaj = (area: SearchArea, zPodpowiedzi: boolean) => {
    // Model zbiorów (zawężanie, pochłanianie, limit) siedzi w src/lib/areaSelection.ts,
    // nie tutaj — ta sama reguła obowiązuje wybór chipem i wybór z podpowiedzi.
    const wynik = dodajObszar(areas, area);
    if (wynik.limitOsiagniety) {
      setLimit(true);
    } else {
      setLimit(false);
      if (wynik.areas !== areas) {
        onChange(wynik.areas);
      }
    }
    pole.wyczysc();
    // Blur NATYCHMIAST po wyborze — inaczej klawiatura zostaje na ekranie nad wynikami.
    inputRef.current?.blur();
    if (zPodpowiedzi && !wynik.limitOsiagniety) onClose();
  };

  /** Miejsce z wyników: jak w „Blisko do…” — obszar `place` i wpis na liście „Ostatnio wybrane”. */
  const wybierzMiejsce = (p: PodpowiedzMiejsca) => {
    dodaj(obszarZMiejsca(p), true);
    ostatnieMiejsca.zapamietaj(p);
  };

  const usun = (key: string) => { setLimit(false); onChange(usunObszar(areas, key)); };

  // Stacje POKAZUJĄ SIĘ W KONTEKŚCIE dzielnicy, a nie jako chmura na wejściu (decyzja Piotra
  // 2026-09-26: 39 chipów stacji zepchnęło wszystko poza ekran). Osiedla rozwija ten sam
  // komponent kaskady, co dymek „Dzielnice” na pulpicie i wiersz Lokalizacja panelu
  // (WyborDzielnic, plan §1.17); stacje dokłada mu nakładka pod osiedlami.
  const stacjeDzielnicy = (nazwa: string) =>
    WARSAW_METRO.filter((s) => WARSAW_METRO_DISTRICT[s.name] === nazwa);

  /** Dotknięcie stacji na pełnej liście: wybiera albo zdejmuje. Ten sam model zbiorów,
   *  co przy chipach — `dodajObszar` zna pochłanianie i limit, `usunObszar` zdejmowanie. */
  const przelaczStacje = (s: StacjaNaLiscie, jestWybrana: boolean) => {
    if (jestWybrana) usun(`m:${s.name}`);
    else dodaj({ kind: 'station', name: s.name, lat: s.lat, lon: s.lon, radiusM: STATION_RADIUS_M }, false);
  };

  // Pasek wybranych obszarów i komunikat o limicie są WSPÓLNE dla obu ekranów: człowiek
  // wybierający stacje ma widzieć to samo, co wybierający dzielnice, i tak samo zdejmować.
  const pasekWybranych = (
    <>
    {limit && (
      <p role="status" className="border-b bg-warning-soft px-4 py-2 text-xs text-warning-soft-foreground">
        {t('search.area.limit_reached', { count: MAX_OBSZAROW })}
      </p>
    )}
    {areas.length > 0 && (
      <div className="flex flex-wrap items-center gap-1.5 border-b px-4 py-2">
        {/* „Cała Warszawa" — zdejmuje wszystkie wybrane obszary naraz. Nazwa miasta z
            `PRIMARY_ACTIVE_CITY` (Konwencja 23), nie zaszyta; forma „Cała {{miasto}}" pasuje
            do Warszawy — przy drugim mieście klucz dostanie rodzaj gramatyczny. */}
        <button
          type="button"
          data-testid="cala-warszawa"
          onClick={() => { setLimit(false); onChange([]); }}
          className="flex items-center rounded-full border border-dashed px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/5"
        >
          {/* Nazwa miasta w mianowniku z tłumaczenia („Варшава"); gdy klucza nie ma (PL, EN),
              zostaje nazwa z geoScope — tak jak dotąd. */}
          {t('search.area.whole_city', {
            miasto: t(`search.area.miasto_mianownik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY }),
          })}
        </button>
        {areas.map((a) => {
          const label = etykietaObszaru(a, t);
          const przygaszony = przygaszone.includes(areaKey(a));
          const swieci = blysk === areaKey(a);
          return (
            <span
              key={areaKey(a)}
              data-testid="chip-nakladki"
              data-przygaszony={przygaszony || undefined}
              data-blysk={swieci || undefined}
              className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium ${klasyZastapienia({
                przygaszony,
                blysk: swieci,
                tlo: 'bg-muted',
              })}`}
            >
              {a.kind === 'station' && <WarsawMetroIcon size={14} />}
              {a.kind === 'place' && <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />}
              {label}
              <button
                type="button"
                aria-label={t('search.area.remove', { name: label })}
                onClick={() => usun(areaKey(a))}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          );
        })}
      </div>
    )}
    </>
  );

  return createPortal(
    <div
      data-testid="area-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('search.area.placeholder')}
      className="fixed inset-0 z-[70] flex flex-col bg-background"
    >
      {ekran === 'stacje' ? (
        <EkranStacjiMetra
          wybrane={wybrane}
          naPrzelacz={przelaczStacje}
          naPowrot={() => setEkran('glowny')}
          pasek={pasekWybranych}
        />
      ) : (
        <>
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('search.area.back')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-muted"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-2xl border bg-white px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => pole.pisz(e.target.value)}
              placeholder={t('search.area.placeholder')}
              // text-base, nie text-sm: poniżej 16 px iPhone powiększa całą stronę przy
              // fokusie (src/lib/mobileInputZoom.guard.test.ts).
              className="min-w-0 flex-1 appearance-none border-0 bg-transparent text-base outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
              // Systemowy krzyżyk pola `search` ukryty (klasy wyżej): obok stoi nasz, a dwa
              // krzyżyki czyszczące to samo pole były widać na iPadzie (28.09).
              type="search"
              name="area-search-overlay"
              role="combobox"
              aria-expanded={pisze}
              aria-autocomplete="list"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              enterKeyHint="search"
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
            />
            {query && (
              <button
                type="button"
                onClick={() => { pole.pisz(''); inputRef.current?.focus(); }}
                aria-label={t('search.area.clear_query')}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {pasekWybranych}

        <div className="min-h-0 flex-1 overflow-y-auto pb-24">
          {pisze && pole.trybMiejsca ? (
            // TRYB MIEJSC (plan §1.90): lista podpowiedzi ustępuje wynikom miejsc.
            <ListaMiejscWPolu
              dotyk
              places={pole.places}
              status={pole.status}
              laduje={pole.laduje}
              onWroc={pole.wroc}
              onWybierz={wybierzMiejsce}
            />
          ) : pisze ? (
            <>
            {podpowiedzi.length === 0 ? (
              // Bez dawnego „Wkrótce wyszukasz też po adresie.” — zastępuje je wiersz „Szukaj miejsca”.
              <div className="px-4 py-4 text-base text-muted-foreground">
                {t('search.area.no_results')}
              </div>
            ) : (
              <>
              {/* Jak w polu na pulpicie (prototyp 19.10): podpowiedź, która przekroczyłaby limit 8,
                  jest wyszarzona od razu, z jedną linią nad listą. Osiedle zastępujące wybraną
                  dzielnicę limitu nie przekracza i zostaje aktywne. */}
              {podpowiedzi.some((s) => !s.disabled && dodajObszar(areas, s.area).limitOsiagniety) && (
                <p role="status" className="bg-warning-soft px-4 py-2 text-xs text-warning-soft-foreground">
                  {t('search.area.limit_reached', { count: MAX_OBSZAROW })}
                </p>
              )}
              <ul>
                {podpowiedzi.map((s) => {
                  const niedostepna = s.disabled || dodajObszar(areas, s.area).limitOsiagniety;
                  return (
                  <li key={areaKey(s.area)}>
                    <button
                      type="button"
                      disabled={niedostepna}
                      onClick={() => !niedostepna && dodaj(s.area, true)}
                      className={`flex min-h-12 w-full items-center gap-2 px-4 py-3 text-left text-base ${
                        niedostepna ? 'cursor-not-allowed opacity-50' : 'active:bg-muted'
                      }`}
                    >
                      {s.type === 'city' ? (
                        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : s.type === 'district' ? (
                        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : s.type === 'station' ? (
                        <WarsawMetroIcon size={16} />
                      ) : (
                        <Home className="h-4 w-4 shrink-0 text-muted-foreground" />
                      )}
                      <span className="min-w-0 flex-1 truncate">
                        {nazwaObszaru(s.area)}
                        {s.sublabel && <span className="text-muted-foreground"> · {s.sublabel}</span>}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {s.disabled ? t('search.area.city_soon') : t(`search.area.${s.type}`)}
                      </span>
                    </button>
                  </li>
                  );
                })}
              </ul>
              </>
            )}
            {/* ZAWSZE ostatni wiersz od 3 znaków; nic nie wyszukuje, dopóki ktoś go nie dotknie. */}
            {/* Blur, jak po wyborze podpowiedzi: klawiatura zasłaniałaby wyniki miejsc. */}
            {pole.wierszMiejsca && (
              <WierszSzukajMiejsca dotyk query={query} onWybierz={() => { pole.szukajMiejsca(); inputRef.current?.blur(); }} />
            )}
            </>
          ) : (
            <>
              {historia.length > 0 && onPrzywroc && (
                <Sekcja tytul={t('search.area.recent')}>
                  <div className="-mx-4 w-[calc(100%+2rem)]">
                    <HistoriaWyszukiwan
                      wpisy={historia}
                      naWybierz={(w) => { onPrzywroc(filtryZWpisu(w)); onClose(); }}
                      naUsun={(q) => setHistoria(usunWpis(q))}
                      naWyczysc={() => setHistoria(wyczyscHistorie())}
                    />
                  </div>
                </Sekcja>
              )}

              {/* Kaskada dzielnic i osiedli — jeden komponent na trzy wejścia (plan §1.17).
                  Osiedla i stacje WYBRANEJ dzielnicy pokazują się dopiero pod nią, bo pokazane
                  z góry (143 osiedla, 39 stacji) spychały wszystko poza ekran. Komunikat o
                  limicie ośmiu obszarów rysuje sam komponent; pasek u góry zostaje dla
                  podpowiedzi i stacji. */}
              <section className="px-4 py-3">
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('search.area.districts_section')}
                </h2>
                <WyborDzielnic
                  areas={areas}
                  onChange={(next) => { setLimit(false); onChange(next); }}
                  onPodglad={setPrzygaszone}
                  dodatkiDzielnicy={(dzielnica) =>
                    stacjeDzielnicy(dzielnica).length > 0 && (
                      <div data-testid="stacje-dzielnicy">
                        <p className="mb-2 text-xs font-medium text-muted-foreground">
                          {t('search.area.stations_in', { name: dzielnica })}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {stacjeDzielnicy(dzielnica).map((st) => (
                            <Chip
                              key={st.name}
                              label={st.name}
                              icon={<WarsawMetroIcon size={14} />}
                              active={wybrane.has(`m:${st.name}`)}
                              onClick={() =>
                                dodaj(
                                  { kind: 'station', name: st.name, lat: st.lat, lon: st.lon, radiusM: STATION_RADIUS_M },
                                  false,
                                )
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )
                  }
                />
              </section>

              {/* WEJŚCIE NA PEŁNĄ LISTĘ STACJI — jedno dotknięcie, osobny ekran. Poziom
                  główny zostaje przy Ostatnio szukane / Dzielnice / Na mapie: chmura 39
                  chipów stacji została stąd usunięta świadomie (decyzja Piotra 26.09) i
                  to wejście jej NIE przywraca. */}
              <Sekcja tytul={t('search.area.metro_section')}>
                <Chip
                  label={t('search.area.stations_all')}
                  icon={<WarsawMetroIcon size={14} />}
                  onClick={() => setEkran('stacje')}
                />
              </Sekcja>

              {(onDrawArea || onPickDistricts) && (
                <Sekcja tytul={t('search.area.on_map')}>
                  {onDrawArea && (
                    <Chip
                      label={t('search.draw.start')}
                      icon={<Pencil className="h-4 w-4" />}
                      onClick={() => { onDrawArea(); onClose(); }}
                    />
                  )}
                  {onPickDistricts && (
                    <Chip
                      label={t('search.districts.start')}
                      icon={<LandPlot className="h-4 w-4" />}
                      onClick={() => { onPickDistricts(); onClose(); }}
                    />
                  )}
                </Sekcja>
              )}
            </>
          )}
        </div>

        </>
      )}

      <div
        className="border-t bg-background px-4 py-3"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="pokaz-wyniki"
          // `tabular-nums` + stała wysokość: cyfry o równej szerokości nie rozciągają
          // napisu przy każdej zmianie liczby, więc przycisk nie drga pod palcem.
          className="flex h-12 w-full items-center justify-center rounded-full bg-foreground text-base font-semibold tabular-nums text-background active:scale-[0.99]"
        >
          {stan.rodzaj === 'liczymy'
            ? '…'
            : stan.rodzaj === 'pusto'
              ? t('search.area.no_offers')
              : t('search.apply_count', { count: stan.ile, liczba: formatLiczby(stan.ile, i18n.language) })}
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default SearchAreaOverlay;
